import { useState } from "react";
import {Mail,Phone,MessageCircle,MapPin,Check,AlertCircle,Loader2,ArrowUpRight,
} from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";

const FORMSPREE_URL = "https://formspree.io/f/maqzvwvr";

const BUDGETS = ["< $5k","$5k – $15k","$15k – $50k","$50k – $150k","$150k+",];
export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", service: "", budget: "", message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();

    setStatus({ state: "loading", msg: "" });

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          _replyto: form.email,
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          service: form.service,
          budget: form.budget,
          message: form.message,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message.");
      }

      setStatus({
        state: "ok",
        msg: "Thank you! Your message has been sent successfully.",
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (error) {
      setStatus({
        state: "err",
        msg: error?.message || "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[#1f1f22] relative overflow-hidden">
        <div className="glow-spot" style={{ width: 700, height: 600, background: "#DC1F33", top: "-200px", left: "-160px" }} />
        <div className="container-x py-24 md:py-32 relative">
          <div className="label-mono mb-6">// Contact / Start Here</div>
          <h1 className="font-display text-[clamp(40px,7vw,96px)] font-black uppercase tracking-tight leading-[0.92] text-white max-w-5xl">
            Let's build<br /><span className="text-[#DC1F33]">something serious.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl">
            Free 30-minute consultation. Honest timeline, transparent pricing, no sales theatre. We reply within one business day.
          </p>
        </div>
      </section>

      <section className="border-b border-[#1f1f22]">
        <div className="container-x py-16 grid lg:grid-cols-[1.4fr_1fr] gap-16">
          {/* Form */}
          <form onSubmit={submit} data-testid="contact-form" className="border border-[#27272A] bg-[#0E0E10] p-1">

            <input
              type="text"
              name="_gotcha"
              style={{ display: "none" }}
              tabIndex="-1"
              autoComplete="off"
            />
            <div className="border border-[#1f1f22] p-8 md:p-10">
              <div className="label-mono mb-6">// 01 — Project brief</div>
              <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight mb-10">Tell us about your project</h2>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="label-mono block mb-2" htmlFor="name">Name *</label>
                  <input data-testid="contact-name" id="name" name="name" required minLength={2} className="field" placeholder="Jane Doe" value={form.name} onChange={update("name")} />
                </div>
                <div>
                  <label className="label-mono block mb-2" htmlFor="email">Email *</label>
                  <input data-testid="contact-email" id="email" name="email" required type="email" className="field" placeholder="jane@company.com" value={form.email} onChange={update("email")} />
                </div>
                <div>
                  <label className="label-mono block mb-2" htmlFor="phone">Phone</label>
                  <input data-testid="contact-phone" id="phone" name="phone" className="field" placeholder="Number" value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label className="label-mono block mb-2" htmlFor="company">Company</label>
                  <input data-testid="contact-company" id="company" name="company" className="field" placeholder="Your business" value={form.company} onChange={update("company")} />
                </div>
                <div>
                  <label className="label-mono block mb-2" htmlFor="service">Service of interest</label>
                  <select data-testid="contact-service" id="service" name="service" className="field" value={form.service} onChange={update("service")}>
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Not sure">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-2" htmlFor="budget">Budget</label>
                  <select data-testid="contact-budget" id="budget" name="budget" className="field" value={form.budget} onChange={update("budget")}>
                    <option value="">Select range…</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="label-mono block mb-2" htmlFor="message">Project details *</label>
                <textarea
                  data-testid="contact-message"
                  id="message"
                  name="message"
                  required
                  minLength={10}
                  rows={6}
                  className="field resize-none"
                  placeholder="Tell us what you're trying to build, the timeline, and any constraints we should know about…"
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              {status.state === "ok" && (
                <div data-testid="contact-success" className="mt-6 flex items-center gap-3 border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 px-4 py-3">
                  <Check size={16} /> <span className="text-sm">{status.msg}</span>
                </div>
              )}
              {status.state === "err" && (
                <div data-testid="contact-error" className="mt-6 flex items-center gap-3 border border-[#F03E3E]/40 bg-[#F03E3E]/10 text-[#F03E3E] px-4 py-3">
                  <AlertCircle size={16} /> <span className="text-sm">{status.msg}</span>
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button data-testid="contact-submit" type="submit" disabled={status.state === "loading"} className="btn-primary disabled:opacity-60">
                  {status.state === "loading" ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : (<>Send Message <ArrowUpRight size={16} /></>)}
                </button>
                <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="btn-ghost">
                  <MessageCircle size={16} /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </form>

          {/* Side info */}
          <div className="space-y-6">
            <div className="border border-[#27272A] bg-[#0E0E10] p-6">
              <div className="label-mono mb-5">// Direct lines</div>
              <ul className="space-y-5">
                <li>
                  <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                    <span className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#DC1F33] group-hover:text-[#DC1F33] text-zinc-300 transition-colors">
                      <Mail size={16} />
                    </span>
                    <span>
                      <div className="label-mono">Email</div>
                      <div className="text-white font-display tracking-tight group-hover:text-[#DC1F33]">{COMPANY.email}</div>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-phone-link" className="flex items-center gap-4 group">
                    <span className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#DC1F33] group-hover:text-[#DC1F33] text-zinc-300 transition-colors">
                      <Phone size={16} />
                    </span>
                    <span>
                      <div className="label-mono">Phone</div>
                      <div className="text-white font-display tracking-tight group-hover:text-[#DC1F33]">{COMPANY.phone}</div>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 group">
                    <span className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#DC1F33] group-hover:text-[#DC1F33] text-zinc-300 transition-colors">
                      <MessageCircle size={16} />
                    </span>
                    <span>
                      <div className="label-mono">WhatsApp</div>
                      <div className="text-white font-display tracking-tight group-hover:text-[#DC1F33]">Chat with our team</div>
                    </span>
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-10 h-10 border border-[#27272A] flex items-center justify-center text-zinc-300">
                    <MapPin size={16} />
                  </span>
                  <span>
                    <div className="label-mono">Locations</div>
                    <div className="text-white font-display tracking-tight">{COMPANY.address}</div>
                  </span>
                </li>
              </ul>
            </div>

            <div className="border border-[#27272A] bg-[#0E0E10] p-6">
              <div className="label-mono mb-4">// What happens next</div>
              <ol className="space-y-4">
                {["We review your brief within 1 business day.", "Schedule a free 30-min discovery call.", "You get a clear roadmap, timeline & fixed quote.", "Decide if we're the right fit — zero pressure."].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="font-display font-bold text-[#DC1F33] w-5 shrink-0">0{i + 1}</span>
                    <span className="text-zinc-300 text-sm leading-relaxed">{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
